import { ProLayout } from '@ant-design/pro-components';
import {
    DashboardOutlined,
    CarOutlined,
    EnvironmentOutlined,
    AimOutlined,
    SendOutlined,
    MonitorOutlined,
    FileTextOutlined,
    GlobalOutlined,
    UserOutlined,
    LogoutOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { Dropdown, Space, Divider, Avatar, message } from 'antd';
import { useIntl } from 'react-intl';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { MenuDataItem } from '@ant-design/pro-components';

import { useLayoutStore, useAuthStore } from '@/store';
import { ConnectionStatus } from '@/components';

const menuItems: MenuDataItem[] = [
    {
        path: '/',
        name: 'menu.dashboard',
        icon: <DashboardOutlined />,
    },
    {
        path: '/vehicles',
        name: 'menu.vehicles',
        icon: <CarOutlined />,
    },
    {
        path: '/stations',
        name: 'menu.stations',
        icon: <EnvironmentOutlined />,
    },
    {
        path: '/stops',
        name: 'menu.stops',
        icon: <AimOutlined />,
    },
    {
        path: '/dispatch',
        name: 'menu.dispatch',
        icon: <SendOutlined />,
    },
    {
        path: '/monitor',
        name: 'menu.monitor',
        icon: <MonitorOutlined />,
    },
    {
        path: '/logs',
        name: 'menu.logs',
        icon: <FileTextOutlined />,
    },
    {
        path: '/settings',
        name: 'menu.settings',
        icon: <SettingOutlined />,
    },
];

const MainLayout: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();
    const { layout, locale, setLocale } = useLayoutStore();
    const { user, logout } = useAuthStore();

    const languageMenu = {
        items: [
            { key: 'zh-CN', label: '简体中文' },
            { key: 'en-US', label: 'English' },
        ],
        onClick: ({ key }: { key: string }) => {
            setLocale(key as 'zh-CN' | 'en-US');
        },
    };

    const userMenu = {
        items: [
            {
                key: 'profile',
                icon: <UserOutlined />,
                label: user?.name || '用户',
                disabled: true,
            },
            { type: 'divider' as const },
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: '退出登录',
                danger: true,
            },
        ],
        onClick: ({ key }: { key: string }) => {
            if (key === 'logout') {
                logout();
                message.success('已退出登录');
                navigate('/login', { replace: true });
            }
        },
    };

    return (
        <ProLayout
            title="Auto Cargo"
            logo="/favicon.svg"
            location={location}
            route={{ routes: menuItems }}
            menuItemRender={(item, dom) => <Link to={item.path!}>{dom}</Link>}
            menuDataRender={() =>
                menuItems.map((item) => ({
                    ...item,
                    name: intl.formatMessage({ id: item.name as string }),
                    locale: false,
                }))
            }
            actionsRender={() => [
                <ConnectionStatus key="connection" />,
                <Divider key="divider1" type="vertical" style={{ margin: '0 8px' }} />,
                <Dropdown key="language" menu={languageMenu}>
                    <Space style={{ cursor: 'pointer' }} role="button" aria-label="切换语言">
                        <GlobalOutlined />
                        {locale === 'zh-CN' ? '中文' : 'EN'}
                    </Space>
                </Dropdown>,
                <Divider key="divider2" type="vertical" style={{ margin: '0 8px' }} />,
                <Dropdown key="user" menu={userMenu}>
                    <Space style={{ cursor: 'pointer' }} role="button" aria-label="用户菜单">
                        <Avatar size="small" icon={<UserOutlined />} />
                        {user?.name || '用户'}
                    </Space>
                </Dropdown>,
            ]}
            {...layout}
        >
            <Outlet />
        </ProLayout>
    );
};

export default MainLayout;
